// "/" — there's no standalone vaults page anymore (decisions/90 IA: the sidebar
// IS the vault list). This resolves the caller's default surface: redirect to
// their first vault, or, when they belong to none, show a no-vault empty state
// (creating one lives here and in the sidebar). New vaults land straight in the
// vault view.

import { Navigate } from "react-router-dom";
import { listVaults } from "../api";
import { useApi, useUser } from "../session";
import { useAsyncData } from "../lib/use-async";
import { useDelayedLoading } from "../lib/use-delayed-loading";
import { hasServerRole } from "../app/roles";
import { CairnMark } from "@/components/cairn-mark";
import { CreateVaultDialog } from "@/components/create-vault-dialog";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from "@/components/ui/empty";

export function VaultIndex() {
  const api = useApi();
  const user = useUser();
  const vaults = useAsyncData(() => listVaults(api), []);
  const showLoading = useDelayedLoading(vaults.loading && !vaults.data);
  const canCreate = hasServerRole(user.server_role, "member");

  if (vaults.error) {
    return (
      <div className="p-6">
        <Alert variant="destructive">
          <AlertTitle>Couldn't load your vaults</AlertTitle>
          <AlertDescription>{vaults.error}</AlertDescription>
        </Alert>
      </div>
    );
  }

  if (!vaults.data) {
    // Delay the skeleton so a fast list load never flashes it.
    return showLoading ? (
      <div className="flex flex-col gap-2 p-6">
        <Skeleton className="h-4 w-40" />
        <Skeleton className="h-4 w-64" />
      </div>
    ) : null;
  }

  const first = vaults.data[0];
  if (first) return <Navigate to={`/v/${first.vault.id}`} replace />;

  // Zero memberships — a graceful empty state.
  return (
    <div className="grid flex-1 place-items-center p-6">
      <Empty className="max-w-md border">
        <EmptyHeader>
          <EmptyMedia variant="icon">
            <CairnMark className="size-5" />
          </EmptyMedia>
          <EmptyTitle>No vaults yet</EmptyTitle>
          <EmptyDescription>
            {canCreate
              ? "You're not in any vault on this server yet. Create your first one to get started — sync a client to it, then invite others."
              : "You're not a member of any vault on this server. Ask a vault owner for an invite."}
          </EmptyDescription>
        </EmptyHeader>
        {canCreate && (
          <EmptyContent>
            <CreateVaultDialog onCreated={vaults.reload} />
          </EmptyContent>
        )}
      </Empty>
    </div>
  );
}
